import type { Scene } from '@seddi/three';
import type { ThreeEnvironment } from '../resources/environmentTypes';
import type { ResourcesContext } from '../resources/resourcesContext';
import {
  type EntityRenderable,
  create as createEntityInternal,
  dispose as disposeEntityInternal,
} from './entityRenderable';

/**
 * Scene renderable.
 */
export interface SceneRenderable {
  readonly entities: Map<string, EntityRenderable>;
  environmentRef?: string;
  readonly scene: Scene;
}

/**
 * Create renderable.
 * @param scene - 3D scene.
 */
export const create = (scene: Scene): SceneRenderable => {
  return { entities: new Map(), scene };
};

/**
 * Create entity.
 * @param id - Entity ID.
 */
export const createEntity = (
  renderable: SceneRenderable,
  id: string,
  parent?: EntityRenderable,
): EntityRenderable => {
  const entity = createEntityInternal(id, parent);
  renderable.entities.set(id, entity);
  renderable.scene.add(entity.node);
  return entity;
};

/**
 * Dispose entity.
 * @param renderable - Renderable.
 * @param id - Entity ID.
 * @param resources - Resources manager.
 */
export const disposeEntity = (
  renderable: SceneRenderable,
  id: string,
  resources: ResourcesContext,
) => {
  const entity = renderable.entities.get(id);
  disposeEntityInternal(entity, resources, (entity) => {
    renderable.entities.delete(entity.id);
  });
};

export const attachEnvironment = (
  renderable: SceneRenderable,
  id: string,
  resources: ResourcesContext,
) => {
  const resource = resources.getRefEnvironment(id)!;
  renderable.scene.environment = resource.texture;
  renderable.environmentRef = resource.id;
};

/**
 * Detach environment.
 * @param renderable - Renderable.
 * @param context - Resources context.
 */
export const detachEnvironment = (renderable: SceneRenderable, context: ResourcesContext) => {
  if (!renderable.environmentRef) {
    return;
  }
  context.retRefEnvironment(renderable.environmentRef);
  delete renderable.environmentRef;
  renderable.scene.environment = null;

  // TODO: Flag existing materials need update.
  for (const entity of renderable.entities) {
    // TODO: Flag existing materials need update.
  }
};
